<section>
  <h1 class="text-center text-3xl font-bold md:text-4xl">
    Generate Android Keystore
  </h1>
  <p class="mx-auto mt-4 text-balance text-center text-xl">
    Fill in the details below to generate a PKCS#12 keystore for signing your
    Android app. Everything runs in your browser — no data is sent to any
    server.
  </p>
  <form
    [formGroup]="formGroup"
    (ngSubmit)="onSubmit()"
    class="mx-auto mt-12 w-full md:w-2/3 lg:w-1/2"
  >
    <div class="tui-form__row flex flex-col gap-4 md:flex-row">
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Full Name <span class="tui-required"></span></label>
          <input
            [formControl]="formGroup.controls.fullName"
            tuiTextfield
            placeholder="John Doe"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="fullNameHint" />
        </tui-textfield>
        <ng-template #fullNameHint>
          The name that will appear in the certificate's Common Name (CN) field.
        </ng-template>
        <tui-error
          formControlName="fullName"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Email <span class="tui-required"></span></label>
          <input
            [formControl]="formGroup.controls.email"
            tuiTextfield
            placeholder="john@example.com"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="emailHint" />
        </tui-textfield>
        <ng-template #emailHint>
          The email address that will be embedded in the certificate.
        </ng-template>
        <tui-error
          formControlName="email"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    </div>
    <div class="tui-form__row flex flex-col gap-4 md:flex-row">
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Organization</label>
          <input
            [formControl]="formGroup.controls.organization"
            tuiTextfield
            placeholder="Acme Inc."
          />
          <tui-icon [tuiTooltip]="organizationHint" />
        </tui-textfield>
        <ng-template #organizationHint>
          The legal name of your company or organization.
        </ng-template>
        <tui-error
          formControlName="organization"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Organizational Unit</label>
          <input
            [formControl]="formGroup.controls.organizationalUnit"
            tuiTextfield
            placeholder="Engineering"
          />
          <tui-icon [tuiTooltip]="organizationalUnitHint" />
        </tui-textfield>
        <ng-template #organizationalUnitHint>
          The department or division within your organization.
        </ng-template>
        <tui-error
          formControlName="organizationalUnit"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    </div>
    <div class="tui-form__row flex flex-col gap-4 md:flex-row">
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>City</label>
          <input
            [formControl]="formGroup.controls.city"
            tuiTextfield
            placeholder="San Francisco"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="cityHint" />
        </tui-textfield>
        <ng-template #cityHint>
          The city where your organization is located.
        </ng-template>
        <tui-error
          formControlName="city"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>State</label>
          <input
            [formControl]="formGroup.controls.state"
            tuiTextfield
            placeholder="California"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="stateHint" />
        </tui-textfield>
        <ng-template #stateHint>
          The state or province where your organization is located.
        </ng-template>
        <tui-error
          formControlName="state"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    </div>
    <div class="tui-form__row">
      <tui-textfield>
        <label tuiLabel>Country (2-letter code)</label>
        <input
          [formControl]="formGroup.controls.country"
          tuiTextfield
          maxlength="2"
          placeholder="US"
        />
        <tui-icon tuiHintDirection="top-left" [tuiTooltip]="countryHint" />
      </tui-textfield>
      <ng-template #countryHint>
        The two-letter ISO 3166-1 alpha-2 country code.
      </ng-template>
      <tui-error
        formControlName="country"
        [error]="[] | tuiFieldError | async"
      />
    </div>
    <div class="tui-form__row flex flex-col gap-4 md:flex-row">
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Password <span class="tui-required"></span></label>
          <input
            [formControl]="formGroup.controls.password"
            tuiTextfield
            type="password"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="passwordHint" />
        </tui-textfield>
        <ng-template #passwordHint>
          Used to protect the keystore. Required when signing your app.
        </ng-template>
        <tui-error
          formControlName="password"
          [error]="[] | tuiFieldError | async"
        />
      </div>
      <div class="flex-1">
        <tui-textfield>
          <label tuiLabel>Key Alias <span class="tui-required"></span></label>
          <input
            [formControl]="formGroup.controls.keyAlias"
            tuiTextfield
            placeholder="my-key-alias"
          />
          <tui-icon tuiHintDirection="top-left" [tuiTooltip]="keyAliasHint" />
        </tui-textfield>
        <ng-template #keyAliasHint>
          A unique name to identify the key entry within the keystore.
        </ng-template>
        <tui-error
          formControlName="keyAlias"
          [error]="[] | tuiFieldError | async"
        />
      </div>
    </div>
    <div class="tui-form__row">
      <tui-textfield>
        <label tuiLabel
          >Validity (years) <span class="tui-required"></span
        ></label>
        <input
          [formControl]="formGroup.controls.validityYears"
          tuiInputNumber
        />
        <tui-icon tuiHintDirection="top-left" [tuiTooltip]="validityHint" />
      </tui-textfield>
      <ng-template #validityHint>
        How long the certificate remains valid. Google recommends at least 25
        years for app signing keys.
      </ng-template>
      <tui-error
        formControlName="validityYears"
        [error]="[] | tuiFieldError | async"
      />
    </div>
    <div class="tui-form__row">
      <label tuiLabel class="flex items-center gap-2">
        <input
          formControlName="newsletter"
          tuiCheckbox
          type="checkbox"
          [size]="'m'"
        />
        <span class="tui-text_body-m">
          Subscribe to the Capawesome Newsletter (no spam, promise!)
        </span>
      </label>
    </div>
    <div class="tui-form__row">
      <label tuiLabel class="flex items-center gap-2">
        <input
          formControlName="privacy"
          tuiCheckbox
          type="checkbox"
          [size]="'m'"
        />
        <span class="tui-text_body-m">
          I've read and accept the
          <a
            href="https://capawesome.io/privacy/"
            target="_blank"
            class="text-[color:var(--tui-text-action)]"
            >Privacy Policy</a
          >.
          <span class="tui-required"></span>
        </span>
      </label>
      <tui-error
        formControlName="privacy"
        [error]="[] | tuiFieldError | async"
      />
    </div>
    <div class="tui-form__row">
      <button
        tuiButton
        type="submit"
        [loading]="loading()"
        class="w-full"
        data-umami-event="tools-android-keystore-generator-download-keystore-button"
      >
        Download Keystore
      </button>
    </div>
  </form>
</section>
